// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HomePage from './components/home_page.tsx';
import { RedirectPage } from './components/redirect_page';
import DailyReadingsRedirect from './components/daily-readings-redirect';
import TranslationSettings from './components/translation-settings';
import './config/i18n';
import i18n from './config/i18n';
import PrivacyPolicy from './components/privacy-policy';
import AccountDeletionPage from './components/account-deletion-page.tsx';
import BetaSubscriptionPage from './components/beta-subscription-page';
import WelcomePage from './components/welcome_page.tsx';
import BibleContentPage from './components/bible_viewer.tsx';
import ReleaseNotes from './components/ReleaseNotes';
import AboutUsPage from './components/about_us_page.tsx';
import UserDocsPage from './components/user_docs_page.tsx';
import EvangileduJour from './pages/EvangileduJour';
import EvangileDetail from './pages/EvangileDetail';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from './supportedLanguages';

// Composant pour injecter les balises hreflang dans le <head>
const HreflangHelmet: React.FC = () => {
  const location = useLocation();

  // Ne pas s'appliquer sur les routes bible (/:lang/:book/:chapter[/:verses])
  // car bible_viewer.tsx gère ses propres hreflang avec les traductions par langue
  const namedRoutes = ['home', 'redirect', 'daily-readings', 'translation-settings', 'privacy', 'account-deletion', 'beta-subscription', 'download', 'welcome', 'bible-content', 'features', 'evangile', 'about-us', 'docs'];
  const pathSegments = location.pathname.split('/').filter(Boolean);
  if (pathSegments.length >= 3 && !namedRoutes.includes(pathSegments[1])) {
    return null;
  }

  // Génère les balises hreflang pour chaque langue supportée
  const hreflangLinks = supportedLanguages.map((lang) => {
    const path = location.pathname.replace(/^\/(fr|en|es|it|de)/, '');
    const href = `${window.location.origin}/${lang}${path}${location.search}`;
    return (
      <link
        key={lang}
        rel="alternate"
        hrefLang={lang}
        href={href}
      />
    );
  });
  const defaultHref = `${window.location.origin}/fr${location.pathname.replace(/^\/(fr|en|es|it|de)/, '')}${location.search}`;
  return (
    <Helmet>
      {hreflangLinks}
      <link rel="alternate" hrefLang="x-default" href={defaultHref} />
    </Helmet>
  );
};

// New component to handle global language routing
const LanguageRouter: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  console.log('[LanguageRouter] render — pathname:', location.pathname);
    React.useEffect(() => {
    // Parse the current path to check the first segment
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const firstSegment = pathSegments[0];
    console.log('[LanguageRouter] effect — firstSegment:', firstSegment, 'pathname:', location.pathname);

    // If path is empty or just "/", redirect to language-specific home
    if (!firstSegment) {
      // Get browser language
      const browserLang = navigator.language || navigator.languages?.[0] || 'fr';
      const langCode = browserLang.split('-')[0].toLowerCase();
      const targetLang = supportedLanguages.includes(langCode) ? langCode : 'fr';
      navigate(`/${targetLang}`, { replace: true });
      return;
    }

    // Prevent redirect for /app route and /daily-readings route
    if (firstSegment === 'app' || firstSegment === 'daily-readings') {
      return;
    }
    
    // If first segment is not a supported language, redirect (preserve query string)
    if (!supportedLanguages.includes(firstSegment)) {
      console.log('[LanguageRouter] Non-language first segment:', firstSegment, 'Full path:', location.pathname, 'Search:', location.search, 'Path segments:', pathSegments);
      const browserLang = navigator.language || navigator.languages?.[0] || 'fr';
      const langCode = browserLang.split('-')[0].toLowerCase();
      const targetLang = supportedLanguages.includes(langCode) ? langCode : 'fr';
      // Reconstruct the full path including all segments
      const restOfPath = '/' + pathSegments.join('/');
      const redirectTo = `/${targetLang}${restOfPath}${location.search}`;
      console.log('[LanguageRouter] Redirecting to:', redirectTo);
      navigate(redirectTo, { replace: true });
      return;
    }
  }, [location.pathname, navigate]);
  
  return <Outlet />;
};

const LanguageLayout: React.FC = () => {
  const { lang } = useParams();
  console.log("LanguageLayout: received lang param:", lang);
  
  React.useEffect(() => {
    if (lang && supportedLanguages.includes(lang) && i18n.language !== lang) {
      console.log(`Changing language to: ${lang}`);
      i18n.changeLanguage(lang);
    }
  }, [lang]);
  
  if (!lang || !supportedLanguages.includes(lang)) {
    console.log(`Language "${lang}" not supported, redirecting to /fr`);
    return <Navigate to="/fr" replace />;
  }
  
  return <Outlet />;
};

// Handle direct Bible URLs (without language prefix)
const RedirectDirectBibleUrl: React.FC = () => {
  const { book, chapter, verses } = useParams();
  const location = useLocation();
  // Parse query string for translation param
  const searchParams = new URLSearchParams(location.search);
  const tParam = searchParams.get('t');

  // Check if the first segment is a supported language code
  const pathSegments = location.pathname.split('/').filter(segment => segment);
  const firstSegment = pathSegments[0];
  console.log('[RedirectDirectBibleUrl] path:', location.pathname, 'search:', location.search, 'params:', { book, chapter, verses }, 'firstSegment:', firstSegment, 'tParam:', tParam);

  // If the first segment is a supported language, don't redirect
  if (supportedLanguages.includes(firstSegment)) {
    console.log(`[RedirectDirectBibleUrl] First segment "${firstSegment}" is a supported language, not redirecting`);
    return <></>;
  }

  console.log(`[RedirectDirectBibleUrl] First segment "${firstSegment}" is not a supported language, treating as book code`);

  // Get browser language
  const browserLang = navigator.language || navigator.languages?.[0] || 'fr';
  const langCode = browserLang.split('-')[0].toLowerCase();
  const targetLang = supportedLanguages.includes(langCode) ? langCode : 'fr';

  let to = `/${targetLang}/${book}/${chapter}`;
  if (verses) to += `/${verses}`;
  // Preserve ?t=... if present
  if (tParam) to += `?t=${tParam}`;

  console.log('[RedirectDirectBibleUrl] Redirecting to:', to);
  return <Navigate to={to} replace />;
};

// Fallback component to handle non-language-prefixed URLs
const LanguageRouterFallback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();  React.useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const firstSegment = pathSegments[0];
    // Skip redirect for /app and /daily-readings routes
    if (firstSegment === 'app' || firstSegment === 'daily-readings') return;
    // Skip redirect for empty path
    if (!firstSegment) return;
    // If already language-prefixed, let React Router handle it — do NOT redirect
    if (supportedLanguages.includes(firstSegment)) return;
    const browserLang = navigator.language || navigator.languages?.[0] || 'fr';
    const langCode = browserLang.split('-')[0].toLowerCase();
    const targetLang = supportedLanguages.includes(langCode) ? langCode : 'fr';
    // Reconstruct the full path including all segments
    const restOfPath = '/' + pathSegments.join('/');
    const redirectTo = `/${targetLang}${restOfPath}${location.search}`;
    console.log('[LanguageRouterFallback] Redirecting to:', redirectTo);
    navigate(redirectTo, { replace: true });
  }, [location.pathname, navigate]);
  return null;
};

const App: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Router>
      <Helmet>
        <title>{t('appTitle') || ''}</title>
        <meta name="description" content={t('appDescription') || ''} />
        <meta property="og:site_name" content="Bible4Me" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://bible4.me/logo%20626x626.png" />
        <meta property="og:title" content={t('appTitle') || ''} />
        <meta property="og:description" content={t('appDescription') || ''} />
      </Helmet>
      <HreflangHelmet />
      <Routes>
        <Route path="/" element={<Navigate to="/fr" replace />} />
        {/* Exceptions: routes that are never rewritten or matched as a language */}
        <Route path="app/*" element={<div id="flutter-app-mount" style={{width: '100vw', height: '100vh'}} />}/>
        <Route path="daily-readings" element={<DailyReadingsRedirect />} />
        <Route element={<LanguageRouter />}> 
          <Route path=":lang" element={<LanguageLayout />}>
            <Route index element={<HomePage />} />
            <Route path="home" element={<HomePage />} />
            <Route path="redirect" element={<RedirectPage />} />
            <Route path="daily-readings" element={<DailyReadingsRedirect />} />
            <Route path="translation-settings" element={<TranslationSettings />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="account-deletion" element={<AccountDeletionPage />} />
            <Route path="beta-subscription" element={<BetaSubscriptionPage />} />
            <Route path="download" element={<BetaSubscriptionPage />} />
            <Route path="welcome" element={<WelcomePage />} />
            <Route path="bible-content" element={<BibleContentPage />} />
            <Route path="features" element={<ReleaseNotes />} />
            <Route path="evangile" element={<EvangileduJour />} />
            <Route path="evangile/:date" element={<EvangileDetail />} />
            <Route path="about-us" element={<AboutUsPage />} />
            <Route path="docs" element={<UserDocsPage />} />
            <Route path="docs/:chapter" element={<UserDocsPage />} />
            {/* Add dynamic Bible reference route */}
            <Route path=":book/:chapter/:verses?" element={<BibleContentPage />} />
          </Route>
        </Route>
        {/* Fallback: let LanguageRouterFallback handle non-lang URLs */}
        <Route path="*" element={<LanguageRouterFallback />} />
      </Routes>
    </Router>
  );
};

export default App;